import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Topbar from '../../layouts/Topbar';
import Sidebar from '../../layouts/Sidebar';
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api';

const API_BASE_URL =
    typeof apiConfig === 'string'
        ? apiConfig
        : NAMED_API_BASE_URL || 'https://pms.burrayhr.com';

/* ------------------------------------------------------------------ */
/*  Shimmer skeleton                                                   */
/* ------------------------------------------------------------------ */
const Shimmer = ({ width = '100%', height = '1rem', className = '', rounded = '4px' }) => (
    <span
        className={`placeholder placeholder-wave col-12 ${className}`}
        style={{ width, height, minHeight: height, borderRadius: rounded, display: 'block' }}
    />
);

const CardSkeleton = ({ delay = 0 }) => (
    <div className="col-md-6 col-xl-4 d-flex" style={{ animationDelay: `${delay}ms` }}>
        <div className="card flex-fill">
            <div className="card-body placeholder-glow">
                <div className="d-flex align-items-center gap-3 mb-3">
                    <Shimmer width="48px" height="48px" rounded="12px" />
                    <div className="flex-grow-1">
                        <Shimmer width="65%" height="14px" className="mb-2" />
                        <Shimmer width="45%" height="12px" />
                    </div>
                </div>
                <Shimmer width="80%" height="12px" className="mb-2" />
                <Shimmer width="55%" height="12px" />
            </div>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Staggered reveal                                                   */
/* ------------------------------------------------------------------ */
const AnimatedCard = ({ children, index = 0, className = '' }) => (
    <div className={`stats-card-reveal ${className}`} style={{ animationDelay: `${index * 70}ms` }}>
        {children}
    </div>
);

/* ------------------------------------------------------------------ */
/*  Colour rotation                                                    */
/* ------------------------------------------------------------------ */
const avatarColors = ['bg-teal', 'bg-warning', 'bg-orange', 'bg-success', 'bg-info', 'bg-indigo', 'bg-pink'];

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
const Departments = () => {
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    /* ── Cost centers (for select) ── */
    const [costCenters, setCostCenters] = useState([]);
    const [costCentersLoading, setCostCentersLoading] = useState(false);

    /* ── Edit modal ── */
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editDepartment, setEditDepartment] = useState(null);
    const [editName, setEditName] = useState('');
    const [editCostCenter, setEditCostCenter] = useState(null);
    const [editSaving, setEditSaving] = useState(false);

    /* ── Delete confirm ── */
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const animStyles = `
        .stats-card-reveal {
            animation: statsFadeInUp 0.45s ease both;
        }
        @keyframes statsFadeInUp {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        .placeholder-glow .placeholder {
            animation: placeholder-glow 1.4s ease-in-out infinite;
        }
        .dept-modal-backdrop {
            position: fixed;
            inset: 0;
            background: rgba(0,0,0,0.45);
            z-index: 1050;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px;
        }
        .dept-modal {
            background: #fff;
            border-radius: 12px;
            padding: 32px;
            width: 100%;
            max-width: 480px;
            box-shadow: 0 16px 48px rgba(0,0,0,0.18);
            position: relative;
            animation: statsFadeInUp 0.3s ease both;
        }
        .dept-card-icon {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            font-size: 16px;
            font-weight: 700;
            color: #fff;
        }
        .dept-hover-card {
            transition: box-shadow 0.2s ease, transform 0.2s ease;
        }
        .dept-hover-card:hover {
            box-shadow: 0 8px 24px rgba(0,0,0,0.10) !important;
            transform: translateY(-2px);
        }
        .delete-confirm-modal {
            max-width: 400px;
        }
    `;

    /* ── Fetch departments ── */
    const fetchDepartments = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/departments/`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setDepartments(Array.isArray(response.data) ? response.data : []);
        } catch {
            setError('Failed to fetch departments.');
            toast.error('Failed to fetch departments.');
        } finally {
            setLoading(false);
        }
    }, []);

    /* ── Fetch cost centers ── */
    const fetchCostCenters = useCallback(async () => {
        setCostCentersLoading(true);
        try {
            const token = Cookies.get('token');
            const response = await axios.get(`${API_BASE_URL}/api/course-centers/`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            setCostCenters(Array.isArray(response.data) ? response.data : []);
        } catch {
            toast.error('Failed to fetch cost centers.');
        } finally {
            setCostCentersLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    /* ── Edit handlers ── */
    const handleEdit = async (dept) => {
        await fetchCostCenters();
        setEditDepartment(dept);
        setEditName(dept.name);
        setEditCostCenter(
            dept.course_center
                ? { value: dept.course_center, label: dept.course_center_name }
                : null
        );
        setEditModalOpen(true);
    };

    const handleEditClose = () => {
        setEditModalOpen(false);
        setEditDepartment(null);
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        setEditSaving(true);
        try {
            const token = Cookies.get('token');
            await axios.put(
                `${API_BASE_URL}/api/departments/${editDepartment.id}/`,
                { name: editName, course_center: editCostCenter?.value },
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
            );
            toast.success('Department updated successfully!');
            handleEditClose();
            fetchDepartments();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to update department.');
        } finally {
            setEditSaving(false);
        }
    };

    /* ── Delete handlers ── */
    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            const token = Cookies.get('token');
            await axios.delete(`${API_BASE_URL}/api/departments/${deleteTarget.id}/`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            });
            toast.success('Department deleted successfully!');
            setDeleteTarget(null);
            fetchDepartments();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete department.');
        } finally {
            setDeleting(false);
        }
    };

    const costCenterOptions = costCenters.map((c) => ({ value: c.id, label: c.name }));

    const getInitials = (name = '') =>
        name
            .split(/\s+/)
            .slice(0, 2)
            .map((w) => w[0]?.toUpperCase())
            .join('');

    return (
        <>
            <style>{animStyles}</style>
            <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} />

            <div
                data-sidebar-skin="dark"
                data-header-skin="light"
                data-navbar-brand-skin="dark"
                data-sidebar-state="expand"
            >
                <Topbar />
                <Sidebar />

                <div className="page-wrapper">
                    <div className="content pb-0">

                        {/* ─── Page Header ─── */}
                        <AnimatedCard index={0}>
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                                <div>
                                    <h4 className="fw-bold mb-1">Departments</h4>
                                    <p className="text-muted mb-0 fs-13">
                                        View and manage all your organisation's departments
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm btn-outline-white border"
                                        onClick={fetchDepartments}
                                        disabled={loading}
                                        title="Refresh"
                                    >
                                        <i
                                            className="ti ti-refresh me-1"
                                            style={{
                                                display: 'inline-block',
                                                transition: 'transform 0.4s',
                                                transform: loading ? 'rotate(360deg)' : 'none',
                                            }}
                                        />
                                        Refresh
                                    </button>
                                    <Link to="/hr/department/add" className="btn btn-sm btn-primary">
                                        <i className="ti ti-plus me-1" />
                                        Add Department
                                    </Link>
                                </div>
                            </div>
                        </AnimatedCard>

                        {/* ─── Content ─── */}
                        {loading ? (
                            <div className="row">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <CardSkeleton key={i} delay={i * 80} />
                                ))}
                            </div>
                        ) : error ? (
                            <AnimatedCard index={1}>
                                <div className="alert alert-danger d-flex align-items-center gap-2 mt-2" role="alert">
                                    <i className="ti ti-alert-circle fs-20" />
                                    <div>
                                        {error}{' '}
                                        <button
                                            className="btn btn-sm btn-outline-danger ms-2"
                                            onClick={fetchDepartments}
                                        >
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            </AnimatedCard>
                        ) : departments.length === 0 ? (
                            <AnimatedCard index={1}>
                                <div className="card">
                                    <div className="card-body text-center py-5">
                                        <i className="ti ti-building-off fs-48 text-muted d-block mb-3" />
                                        <h5 className="fw-semibold text-muted mb-2">No Departments Found</h5>
                                        <p className="text-muted fs-13 mb-3">
                                            Get started by adding your first department.
                                        </p>
                                        <Link to="/hr/department/add" className="btn btn-primary btn-sm">
                                            <i className="ti ti-plus me-1" />
                                            Add Department
                                        </Link>
                                    </div>
                                </div>
                            </AnimatedCard>
                        ) : (
                            <div className="row">
                                {departments.map((dept, idx) => {
                                    const colorClass = avatarColors[idx % avatarColors.length];
                                    const initials = getInitials(dept.name);
                                    const employeeCount =
                                        dept.employee_count !== undefined
                                            ? dept.employee_count
                                            : dept.employees?.length ?? 'N/A';

                                    return (
                                        <div key={dept.id || idx} className="col-md-6 col-xl-4 d-flex">
                                            <AnimatedCard index={idx + 1} className="flex-fill">
                                                <div className="card flex-fill dept-hover-card shadow-sm h-100">
                                                    <div className="card-body">
                                                        {/* Top: icon + name + actions */}
                                                        <div className="d-flex align-items-start gap-3 mb-3">
                                                            <div className={`dept-card-icon ${colorClass}`}>
                                                                {initials}
                                                            </div>
                                                            <div className="flex-grow-1 min-w-0">
                                                                <h6 className="fw-bold mb-0 text-truncate">
                                                                    {dept.name}
                                                                </h6>
                                                                {dept.course_center_name && (
                                                                    <span className="badge badge-soft-primary fs-11 mt-1">
                                                                        {dept.course_center_name}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-1">
                                                                <button
                                                                    className="btn btn-sm btn-icon btn-outline-white border"
                                                                    onClick={() => handleEdit(dept)}
                                                                    title="Edit"
                                                                >
                                                                    <i className="ti ti-edit" />
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-icon btn-outline-danger border-0"
                                                                    onClick={() => setDeleteTarget(dept)}
                                                                    title="Delete"
                                                                >
                                                                    <i className="ti ti-trash" />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Stats */}
                                                        <div className="d-flex align-items-center gap-2 pt-2 border-top">
                                                            <div className="avatar avatar-sm bg-teal text-white d-flex align-items-center justify-content-center rounded-circle">
                                                                <i className="ti ti-users fs-13" />
                                                            </div>
                                                            <div>
                                                                <p className="fs-11 text-muted mb-0">Employees</p>
                                                                <p className="fs-13 fw-semibold mb-0">{employeeCount}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </AnimatedCard>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                        <p className="text-dark mb-0">
                            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
                        </p>
                    </div>
                </div>
            </div>

            {/* ─── Edit Modal ─── */}
            {editModalOpen && (
                <div className="dept-modal-backdrop" onClick={handleEditClose}>
                    <div className="dept-modal" onClick={(e) => e.stopPropagation()}>
                        {/* Close btn */}
                        <button
                            className="btn btn-sm btn-icon btn-outline-white border position-absolute"
                            style={{ top: 16, right: 16 }}
                            onClick={handleEditClose}
                            disabled={editSaving}
                            title="Close"
                        >
                            <i className="ti ti-x" />
                        </button>

                        {/* Header */}
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <div className="avatar avtar-lg bg-primary">
                                <i className="ti ti-building fs-22 text-white" />
                            </div>
                            <div>
                                <h5 className="fw-bold mb-0">Edit Department</h5>
                                <p className="text-muted fs-13 mb-0">Update department details</p>
                            </div>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleEditSave}>
                            {/* Department name */}
                            <div className="mb-3">
                                <label htmlFor="editDeptName" className="form-label fw-semibold">
                                    Department Name <span className="text-danger">*</span>
                                </label>
                                <input
                                    id="editDeptName"
                                    type="text"
                                    className="form-control"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    required
                                    disabled={editSaving}
                                    placeholder="e.g. Human Resources"
                                    autoFocus
                                />
                            </div>

                            {/* Cost center select */}
                            <div className="mb-4">
                                <label htmlFor="editDeptCostCenter" className="form-label fw-semibold">
                                    Cost Center <span className="text-danger">*</span>
                                </label>
                                <Select
                                    inputId="editDeptCostCenter"
                                    classNamePrefix="react-select"
                                    isLoading={costCentersLoading}
                                    isDisabled={editSaving || costCentersLoading}
                                    options={costCenterOptions}
                                    value={editCostCenter}
                                    onChange={setEditCostCenter}
                                    placeholder={
                                        costCentersLoading ? 'Loading cost centers…' : 'Select a cost center'
                                    }
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: '40px',
                                            borderRadius: '6px',
                                            borderColor: state.isFocused ? '#4F46E5' : '#dee2e6',
                                            boxShadow: state.isFocused
                                                ? '0 0 0 3px rgba(79,70,229,0.15)'
                                                : 'none',
                                            fontSize: '14px',
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            background: state.isSelected
                                                ? '#4F46E5'
                                                : state.isFocused
                                                ? '#e0e7ff'
                                                : '#fff',
                                            color: state.isSelected ? '#fff' : '#1e293b',
                                            fontSize: '14px',
                                        }),
                                    }}
                                />
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleEditClose}
                                    disabled={editSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={editSaving || !editName.trim() || !editCostCenter}
                                >
                                    {editSaving ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-2"
                                                role="status"
                                                aria-hidden="true"
                                            />
                                            Saving…
                                        </>
                                    ) : (
                                        <>
                                            <i className="ti ti-device-floppy me-1" />
                                            Save Changes
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ─── Delete Confirm Modal ─── */}
            {deleteTarget && (
                <div className="dept-modal-backdrop" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div
                        className="dept-modal delete-confirm-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Icon */}
                        <div className="text-center mb-3">
                            <div
                                className="avatar avtar-lg bg-danger mx-auto mb-3"
                                style={{ width: 56, height: 56 }}
                            >
                                <i className="ti ti-trash fs-24 text-white" />
                            </div>
                            <h5 className="fw-bold mb-1">Delete Department</h5>
                            <p className="text-muted fs-13 mb-0">
                                Are you sure you want to delete{' '}
                                <strong>&ldquo;{deleteTarget.name}&rdquo;</strong>?
                                <br />
                                This action cannot be undone.
                            </p>
                        </div>

                        <div className="d-flex gap-2 justify-content-center mt-4">
                            <button
                                className="btn btn-light"
                                onClick={() => setDeleteTarget(null)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                            >
                                {deleting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        Deleting…
                                    </>
                                ) : (
                                    <>
                                        <i className="ti ti-trash me-1" />
                                        Yes, Delete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Departments;